import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
// utils
import localStorageAvailable from '../utils/localStorageAvailable';
//
import { setSession } from './utils';
import { authLogin, authLogout, authMe, authRegister } from '../api/apiAuth';
// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === 'DELETE_ACCOUNT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const storageAvailable = localStorageAvailable();

  const sanitizeUserForStorage = (user) => {
    if (!user) return null;
    const {
      // Remove abilities from storage (will be stored separately)
      abilities,
      ...safe
    } = user;
    return safe;
  };

  // const initialize = useCallback(async () => {
  //   try {
  //     const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
  //     const user = storageAvailable ? JSON.parse(localStorage.getItem('user')) : '';

  //     if (accessToken && user) {
  //       setSession(accessToken);

  //       dispatch({
  //         type: 'INITIAL',
  //         payload: {
  //           isAuthenticated: true,
  //           user,
  //         },
  //       });
  //     } else {
  //       dispatch({
  //         type: 'INITIAL',
  //         payload: {
  //           isAuthenticated: false,
  //           user: null,
  //         },
  //       });
  //     }
  //   } catch (error) {
  //     console.error(error);
  //     dispatch({
  //       type: 'INITIAL',
  //       payload: {
  //         isAuthenticated: false,
  //         user: null,
  //       },
  //     });
  //   }
  // }, [storageAvailable]);
  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';

      if (accessToken) {
        setSession(accessToken);

        // استخدام authMe من apiAuth
        const response = await authMe();

        if (response.success && response.data) {
          const userData = response.data;

          // حفظ abilities
          if (storageAvailable) {
            localStorage.setItem('abilities', JSON.stringify(userData.abilities || []));

            // حفظ اللغة المفضلة
            if (userData.lang) {
              i18n.changeLanguage(userData.lang);
            }

            const safeUser = sanitizeUserForStorage(userData);
            localStorage.setItem('user', JSON.stringify(safeUser));
          }

          dispatch({
            type: 'INITIAL',
            payload: {
              isAuthenticated: true,
              user: userData,
            },
          });
          return;
        }
      }

      // في حال عدم وجود توكن أو عدم وجود user صحيح
      setSession(null);
      if (storageAvailable) {
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('abilities');
      }
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    } catch (error) {
      console.error('🚀 ~ initialize error:', error);

      setSession(null);
      if (storageAvailable) {
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('abilities');
      }
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable, i18n]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // REGISTER
  const register = useCallback(async (name, email, phone_number, password, password_confirmation, role = 'customer') => {
    try {
      queryClient.clear();
      // استخدام authRegister من apiAuth
      const response = await authRegister({
        name,
        email,
        phone_number,
        password,
        password_confirmation,
        role
      });

      if (response.success && response.data) {
        const { token, user: userData } = response.data;

        // حفظ Token
        setSession(token);
        if (storageAvailable) {
          localStorage.setItem('accessToken', token);

          // حفظ abilities
          localStorage.setItem('abilities', JSON.stringify(userData.abilities || []));

          // حفظ اللغة المفضلة
          if (userData.lang) {
            i18n.changeLanguage(userData.lang);
          }

          const safeUser = sanitizeUserForStorage(userData);
          localStorage.setItem('user', JSON.stringify(safeUser));
        }

        dispatch({
          type: 'REGISTER',
          payload: { user: userData },
        });

        return {
          status: 'success',
          message: response.message || 'Registration successful',
          data: userData,
        };
      } else {
        return {
          status: 'error',
          message: response.message || 'Registration failed',
        };
      }
    }
    catch (error) {
      console.error('🚀 ~ register error:', error);
      return {
        status: 'error',
        message: error?.response?.data?.message || 'حدث خطأ أثناء التسجيل',
      };
    }
  }, [storageAvailable, i18n, queryClient]);

  const login = useCallback(async (email, password) => {
    try {
      queryClient.clear();
      // استخدام authLogin من apiAuth
      const response = await authLogin({ email, password });

      if (response.success && response.data) {
        const { token, user: userData } = response.data;

        // حفظ Token
        setSession(token);
        if (storageAvailable) {
          localStorage.setItem('accessToken', token);

          // حفظ abilities
          localStorage.setItem('abilities', JSON.stringify(userData.abilities || []));

          // حفظ اللغة المفضلة
          if (userData.lang) {
            i18n.changeLanguage(userData.lang);
          }

          const safeUser = sanitizeUserForStorage(userData);
          localStorage.setItem('user', JSON.stringify(safeUser));
        }

        dispatch({
          type: 'LOGIN',
          payload: { user: userData },
        });

        return {
          status: 'success',
          message: response.message || 'Login successful',
          data: userData,
        };
      } else {
        return {
          status: 'error',
          message: response.message || 'Login failed',
        };
      }
    } catch (error) {
      console.error('🚀 ~ login error:', error);
      return {
        status: 'error',
        message:
          error?.response?.data?.message ||
          'البريد الألكتروني او كلمة المرور غير صحيحة',
      };
    }
  }, [storageAvailable, i18n, queryClient]);


  // LOGOUT
  const logout = useCallback(async () => {
    try {
      await authLogout();
    } catch (error) {
      console.error('🚀 ~ logout error:', error);
    } finally {
      setSession(null);
      if (storageAvailable) {
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('abilities');
      }
      queryClient.clear();
      dispatch({
        type: 'LOGOUT',
      });
    }
  }, [storageAvailable, queryClient]);

  const loginWithOtp = useCallback(async (responseData) => {
    queryClient.clear();
    const { token, user: userData } = responseData;

    // حفظ Token
    setSession(token);
    if (storageAvailable) {
      localStorage.setItem('accessToken', token);

      // حفظ abilities
      localStorage.setItem('abilities', JSON.stringify(userData.abilities || []));

      // حفظ اللغة المفضلة
      if (userData.lang) {
        i18n.changeLanguage(userData.lang);
      }

      const safeUser = sanitizeUserForStorage(userData);
      localStorage.setItem('user', JSON.stringify(safeUser));
    }

    dispatch({
      type: 'LOGIN',
      payload: { user: userData },
    });
  }, [storageAvailable, i18n, queryClient]);

  const completeRegisterWithOtp = useCallback(async (responseData) => {
    queryClient.clear();
    const { token, user: userData } = responseData;

    // حفظ Token
    setSession(token);
    if (storageAvailable) {
      localStorage.setItem('accessToken', token);

      // حفظ abilities
      localStorage.setItem('abilities', JSON.stringify(userData.abilities || []));

      // حفظ اللغة المفضلة
      if (userData.lang) {
        i18n.changeLanguage(userData.lang);
      }

      const safeUser = sanitizeUserForStorage(userData);
      localStorage.setItem('user', JSON.stringify(safeUser));
    }

    dispatch({
      type: 'REGISTER',
      payload: { user: userData },
    });
  }, [storageAvailable, i18n, queryClient]);

  const updateUser = useCallback(async (userData) => {
    if (storageAvailable) {
      const safeUser = sanitizeUserForStorage(userData);
      localStorage.setItem('user', JSON.stringify(safeUser));

      if (userData.abilities) {
        localStorage.setItem('abilities', JSON.stringify(userData.abilities));
      }
    }

    dispatch({
      type: 'LOGIN', // Reuse LOGIN action to update user data
      payload: { user: userData },
    });
  }, [storageAvailable]);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      register,
      login,
      logout,
      loginWithOtp,
      completeRegisterWithOtp,
      updateUser
    }),
    [state.isAuthenticated, state.isInitialized, state.user, register, login, logout, loginWithOtp, completeRegisterWithOtp, updateUser]
  );
  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}